import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, ShieldCheck } from "lucide-react";
import CheckoutConfirmDialog from "@/components/CheckoutConfirmDialog";
import ContactSalesDialog from "@/components/ContactSalesDialog";

export default function Pricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pricing/plans");
        setPlans(data.plans);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openCheckout = (plan) => {
    if (!user) {
      window.location.href = "/register";
      return;
    }
    setSelectedPlan(plan);
    setDialogOpen(true);
  };

  // Redirect the logged-in customer to Stripe's hosted Customer Portal so
  // they can update card, download invoices, change plan or cancel. Backend
  // mints a fresh portal session tied to their stripe_customer_id.
  const [portalLoading, setPortalLoading] = useState(false);
  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/portal",
        { return_url: `${window.location.origin}/pricing` });
      window.location.href = data.portal_url;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not open billing portal";
      // Import lazily so this stays a small change with no top-level side-effects.
      const { toast } = await import("sonner");
      toast.error(msg);
      setPortalLoading(false);
    }
  };

  const hasPaidPlan = user && user.plan && user.plan !== "free";

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="vq-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-slate-900 rounded flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-extrabold text-xl">VitaQMS</span>
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-slate-600 hover:text-slate-900">Home</Link>
            {user ? (
              <Link to="/dashboard"><Button className="bg-slate-900 hover:bg-[#0055FF] text-white">Dashboard</Button></Link>
            ) : (
              <Link to="/login" className="text-sm font-medium text-slate-600 hover:text-slate-900">Sign in</Link>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-16 max-w-2xl mx-auto">
          <div className="vq-overline mb-3">Pricing</div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-slate-900 mb-4">
            Simple pricing.<br />Compliance-grade output.
          </h1>
          <p className="text-slate-600">India + US billing supported via Stripe. Cancel anytime.</p>
          <div className="mt-5 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-200">
            <span className="text-xs font-mono uppercase tracking-wider text-emerald-700">Launch offer</span>
            <span className="text-sm text-emerald-800">
              Use code <b className="font-mono">TRIAL30</b> at checkout for a 30-day free trial.
            </span>
          </div>
          {hasPaidPlan && (
            <div className="mt-6 flex items-center justify-center gap-3" data-testid="billing-portal-callout">
              <span className="text-sm text-slate-600">
                On <b className="capitalize">{user.plan}</b> plan — manage payment method, invoices, or cancel:
              </span>
              <Button
                data-testid="manage-billing-btn"
                onClick={openBillingPortal}
                disabled={portalLoading}
                variant="outline"
                className="h-9 border-slate-300 text-slate-900 font-semibold"
              >
                {portalLoading ? "Opening…" : "Manage billing →"}
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center text-slate-500">Loading plans…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {plans.filter((p) => p.id !== "free").map((p) => (
              <div
                key={p.id}
                data-testid={`pricing-plan-${p.id}`}
                className={`vq-card p-8 flex flex-col ${p.highlighted ? "border-slate-900 border-2 relative" : ""}`}
              >
                {p.highlighted && (
                  <span className="absolute -top-3 left-8 bg-[#0055FF] text-white text-xs font-semibold px-3 py-1 rounded font-mono uppercase tracking-wider">
                    Most popular
                  </span>
                )}
                <div className="vq-overline mb-2">{p.name}</div>
                <div className="font-display font-extrabold text-4xl text-slate-900 mb-1">{p.price_display}</div>
                {p.yearly_display && <div className="text-sm text-slate-500 font-mono mb-4">or {p.yearly_display}</div>}
                <ul className="space-y-3 mb-8 mt-6 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                {p.id === "enterprise" ? (
                  <Button
                    onClick={() => setContactOpen(true)}
                    data-testid="upgrade-enterprise-btn"
                    className={`w-full h-11 font-semibold ${p.highlighted ? "bg-slate-900 hover:bg-[#0055FF] text-white" : "bg-white border border-slate-300 text-slate-900 hover:border-slate-900"}`}
                  >
                    Talk to sales
                  </Button>
                ) : p.lookup_key ? (
                  <Button
                    onClick={() => openCheckout(p)}
                    disabled={user?.plan === p.id}
                    data-testid={`upgrade-${p.id}-btn`}
                    className={`w-full h-11 font-semibold ${p.highlighted ? "bg-slate-900 hover:bg-[#0055FF] text-white" : "bg-white border border-slate-300 text-slate-900 hover:border-slate-900"}`}
                  >
                    {user?.plan === p.id ? "Current plan" : p.cta}
                  </Button>
                ) : (
                  <Button disabled className="w-full h-11 bg-slate-100 text-slate-500 font-semibold">
                    {user?.plan === "free" ? "Current plan" : "Free tier"}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-slate-500 text-center mt-10 max-w-2xl mx-auto">
          Payments processed by Stripe. No card data stored on our servers (PCI-DSS compliant).
          Tax calculation and remittance handled automatically in supported jurisdictions.
        </p>
      </div>

      <CheckoutConfirmDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        plan={selectedPlan}
      />
      <ContactSalesDialog open={contactOpen} onOpenChange={setContactOpen} />
    </div>
  );
}
